// catmint/error/server-fn-error — Client-side error from server function calls
//
// When a server function fails (either from a sanitized error or a
// ClientSafeError), the client-side fetch stub reconstructs the error
// as a ServerFnError. This provides typed access to statusCode and data.
"use client";
/**
 * Error received on the client from a server function call.
 *
 * This class is constructed by the framework's client-side fetch stubs
 * when a server function returns or throws an error. It provides structured
 * access to the HTTP status code and optional data payload.
 *
 * - When a server function **throws** a `ClientSafeError`, the client
 *   receives a `ServerFnError` via the rejection (throw) path.
 * - When a server function **returns** a `ClientSafeError`, the client
 *   receives a `ServerFnError` as the return value (no throw).
 * - When any other error is thrown, the client receives a `ServerFnError`
 *   with a sanitized message (production) or the original message (development).
 *
 * @example
 * ```ts
 * import { ServerFnError } from 'catmint/error'
 *
 * try {
 *   await transferFunds({ amount: 1000 })
 * } catch (err) {
 *   if (err instanceof ServerFnError) {
 *     console.log(err.statusCode) // 422
 *     console.log(err.data)       // { available: 50 }
 *   }
 * }
 *
 * // Or with return-pattern:
 * const result = await validateForm(formData)
 * if (result instanceof ServerFnError) {
 *   showFieldErrors(result.data?.issues)
 * }
 * ```
 */
export class ServerFnError extends Error {
    /**
     * HTTP status code from the server response.
     */
    statusCode;
    /**
     * Optional structured data payload from the server.
     */
    data;
    constructor(message, statusCode, data) {
        super(message);
        this.name = "ServerFnError";
        this.statusCode = statusCode;
        this.data = data;
        // Maintain proper prototype chain for instanceof checks
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
//# sourceMappingURL=server-fn-error.js.map